// V14.13.8 — Upload Quarantine admin page
//
// Shows every upload rejected by upload_security.validate_upload over the
// past N days (default 7). Useful for spotting probing attempts against
// guest links and bulk-import endpoints. Read-only.
import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Shield, RefreshCw, AlertTriangle, Globe, FileWarning } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const formatTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}`;
};

const formatBytes = (n) => {
  if (!n) return '0 B';
  const u = ['B', 'KB', 'MB', 'GB'];
  let v = n, i = 0;
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(1)} ${u[i]}`;
};

const UploadQuarantinePage = () => {
  const [days, setDays] = useState('7');
  const [endpointFilter, setEndpointFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ total: 0, entries: [], summary: { by_reason: [], by_ip: [] } });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ days, limit: '200' });
      if (endpointFilter) params.append('endpoint', endpointFilter);
      const token = localStorage.getItem('ems_token');
      const r = await fetch(`${API_URL}/admin/upload-quarantine?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${r.status}`);
      }
      setData(await r.json());
    } catch (err) {
      toast.error(err.message || 'Failed to load quarantine');
    } finally {
      setLoading(false);
    }
  }, [days, endpointFilter]);

  useEffect(() => { load(); }, [load]);

  const { total, entries, summary } = data;

  return (
    <div className="p-6">
      <div className="space-y-6" data-testid="upload-quarantine-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Shield className="w-6 h-6 text-red-500" />
              Upload Quarantine
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Files rejected by the upload validator. Retained for 7 days.
            </p>
          </div>
          <Button onClick={load} disabled={loading} variant="outline" size="sm" data-testid="quarantine-refresh-btn">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-6 flex flex-wrap items-end gap-3">
            <div className="space-y-1.5">
              <label className="text-xs uppercase tracking-wider text-muted-foreground">Window</label>
              <Select value={days} onValueChange={setDays}>
                <SelectTrigger className="w-32 h-9" data-testid="quarantine-days-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">Last 24 h</SelectItem>
                  <SelectItem value="3">Last 3 days</SelectItem>
                  <SelectItem value="7">Last 7 days</SelectItem>
                  <SelectItem value="14">Last 14 days</SelectItem>
                  <SelectItem value="30">Last 30 days</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5 flex-1 min-w-[220px]">
              <label className="text-xs uppercase tracking-wider text-muted-foreground">Endpoint filter</label>
              <Input
                placeholder="e.g. guest, hub, ems"
                value={endpointFilter}
                onChange={(e) => setEndpointFilter(e.target.value)}
                className="h-9"
                data-testid="quarantine-endpoint-filter"
              />
            </div>
            <div className="text-sm text-muted-foreground">
              <Badge variant="outline" className="bg-muted/50" data-testid="quarantine-total-count">
                {total} rejected
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* Summary */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <FileWarning className="w-4 h-4 text-orange-500" /> Top reasons
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-1.5">
              {summary.by_reason.length === 0 && <p className="text-sm text-muted-foreground">No rejections in window.</p>}
              {summary.by_reason.map((r, i) => (
                <div key={i} className="flex items-start justify-between gap-3 text-sm">
                  <span className="text-muted-foreground line-clamp-2 flex-1">{r.reason}</span>
                  <Badge variant="outline" className="shrink-0">{r.count}</Badge>
                </div>
              ))}
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Globe className="w-4 h-4 text-blue-500" /> Top IPs
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-1.5">
              {summary.by_ip.length === 0 && <p className="text-sm text-muted-foreground">No rejections in window.</p>}
              {summary.by_ip.map((r, i) => (
                <div key={i} className="flex items-center justify-between gap-3 text-sm">
                  <code className="text-xs">{r.ip}</code>
                  <Badge variant="outline" className="shrink-0">{r.count}</Badge>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>

        {/* Table */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-red-500" /> Recent rejections
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="quarantine-table">
                <thead className="bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="text-left px-4 py-2">Time</th>
                    <th className="text-left px-4 py-2">File</th>
                    <th className="text-left px-4 py-2">Size</th>
                    <th className="text-left px-4 py-2">Endpoint</th>
                    <th className="text-left px-4 py-2">IP</th>
                    <th className="text-left px-4 py-2">Reason</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {entries.length === 0 && (
                    <tr><td colSpan={6} className="text-center text-muted-foreground py-8">No rejections in this window.</td></tr>
                  )}
                  {entries.map((e, i) => (
                    <tr key={i} className="hover:bg-muted/30">
                      <td className="px-4 py-2 whitespace-nowrap text-xs">{formatTime(e.created_at)}</td>
                      <td className="px-4 py-2 max-w-[200px] truncate" title={e.filename}>{e.filename || '—'}</td>
                      <td className="px-4 py-2 whitespace-nowrap">{formatBytes(e.size)}</td>
                      <td className="px-4 py-2 whitespace-nowrap text-xs"><code>{e.endpoint}</code></td>
                      <td className="px-4 py-2 whitespace-nowrap"><code className="text-xs">{e.ip || '—'}</code></td>
                      <td className="px-4 py-2 text-xs text-orange-600 dark:text-orange-400">{e.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default UploadQuarantinePage;
